import { Typography } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Domain } from '../../../../types.generated';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { RotatingTriangle } from '../../../shared/sidebar/components';
import DomainIcon from '../../DomainIcon';
import useListDomainsForNav from '../../useListDomainsForNav';
import { DOMAIN_NODE_CHILDREN_PAGE_SIZE } from '../../utils';
import useToggle from '../../../shared/useToggle';
import { BodyContainer, BodyGridExpander } from '../../../shared/components';
import { ANTD_GRAY_V2, DARK_BLACK } from '../../../entity/shared/constants';
import { useDomainsContext } from '../../DomainsContext';
import { applyOpacity } from '../../../shared/styleUtils';
import useHasDomainChildren from './useHasDomainChildren';

const RowWrapper = styled.div`
    align-items: center;
    display: flex;
    overflow: hidden;
    padding: 2px 2px 4px 2px;
`;

const NameWrapper = styled(Typography.Text)<{ isSelected: boolean; addLeftPadding: boolean }>`
    flex: 1;
    padding: 4px;
    overflow: hidden;
    ${(props) =>
        props.isSelected && `background-color: ${applyOpacity(props.theme.styles['primary-color'] || '', 10)};`}
    ${(props) => props.addLeftPadding && 'padding-left: 24px;'}

    &:hover {
        ${(props) => !props.isSelected && `background-color: ${ANTD_GRAY_V2[1]};`}
        cursor: pointer;
    }

    svg {
        margin-right: 6px;
    }

    &&.ant-typography {
        color: ${DARK_BLACK[10]} !important;
    }
`;

const ButtonWrapper = styled.span`
    margin-right: 4px;
    font-size: 16px;
    height: 16px;
    width: 16px;

    svg {
        height: 10px;
        width: 10px;
    }

    .ant-btn {
        height: 16px;
        width: 16px;
    }
`;

const StyledExpander = styled(BodyGridExpander)`
    padding-left: 24px;
`;

interface Props {
    domain: Domain;
    numDomainChildren: number;
    domainUrnToHide?: string;
    selectDomainOverride?: (domain: Domain) => void;
    showIcon?: boolean;
    isDomainAccessible?: boolean;
}

export default function DomainNode({
    domain,
    numDomainChildren,
    domainUrnToHide,
    selectDomainOverride,
    showIcon,
    isDomainAccessible,
}: Props) {
    const shouldHideDomain = domainUrnToHide === domain.urn;
    const history = useHistory();
    const entityRegistry = useEntityRegistry();
    const { entityData } = useDomainsContext();
    const { isOpen, isClosing, toggle, toggleOpen } = useToggle({
        initialValue: false,
        closeDelay: 250,
    });
    const { domains } = useListDomainsForNav({
        parentDomain: domain.urn,
        skip: !isOpen || shouldHideDomain,
        pageSize: DOMAIN_NODE_CHILDREN_PAGE_SIZE,
    });
    const isOnEntityPage = entityData && entityData.urn === domain.urn;
    const displayName = entityRegistry.getDisplayName(domain.type, isOnEntityPage ? entityData : domain);
    const isInSelectMode = !!selectDomainOverride;
    const hasDomainChildren = useHasDomainChildren({ domainUrn: domain.urn, numDomainChildren });

    const shouldAutoOpen = useMemo(
        () => !isInSelectMode && entityData?.parentDomains?.domains.some((parent) => parent.urn === domain.urn),
        [isInSelectMode, entityData, domain.urn],
    );

    useEffect(() => {
        if (shouldAutoOpen) toggleOpen();
    }, [shouldAutoOpen, toggleOpen]);

    function handleSelectDomain() {
        if (selectDomainOverride) {
            selectDomainOverride(domain);
        } else {
            history.push(entityRegistry.getEntityUrl(domain.type, domain.urn));
        }
    }

    function handleRowKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleSelectDomain();
        }
        // Arrow keys for expand/collapse when domain has children
        else if (hasDomainChildren) {
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                toggle();
            }
        }
    }

    function handleTriangleKeyDown(event: React.KeyboardEvent) {
        if (hasDomainChildren && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            toggle();
        }
    }

    if (shouldHideDomain) return null;

    return (
        <>
            <RowWrapper
                tabIndex={isDomainAccessible === false ? -1 : 0}
                data-testid="domain-list-item"
                onKeyDown={handleRowKeyDown}
            >
                {hasDomainChildren && (
                    <ButtonWrapper tabIndex={-1} onClick={(e) => e.stopPropagation()} onKeyDown={handleTriangleKeyDown}>
                        <RotatingTriangle isOpen={isOpen && !isClosing} onClick={toggle} />
                    </ButtonWrapper>
                )}
                <NameWrapper
                    ellipsis
                    title={displayName}
                    onClick={handleSelectDomain}
                    addLeftPadding={!hasDomainChildren}
                    isSelected={!!isOnEntityPage && !isInSelectMode}
                >
                    {(!isInSelectMode || showIcon) && <DomainIcon ariaLabel={displayName} />}
                    {displayName}
                </NameWrapper>
            </RowWrapper>
            <StyledExpander isOpen={isOpen && !isClosing}>
                <BodyContainer style={{ overflow: 'hidden' }}>
                    {domains?.map((childDomain) => (
                        <DomainNode
                            key={childDomain.urn}
                            showIcon={showIcon}
                            domain={childDomain as Domain}
                            numDomainChildren={childDomain.children?.total || 0}
                            domainUrnToHide={domainUrnToHide}
                            selectDomainOverride={selectDomainOverride}
                            isDomainAccessible={isDomainAccessible}
                        />
                    ))}
                </BodyContainer>
            </StyledExpander>
        </>
    );
}
